import { mergePublicPresence } from "./city-day-presentation";
import L from "leaflet";
import { drawCityDayMap } from "./city-day-map";
import { standardCityMapCenters } from "./city-map-centers";

export type TimelineItem = {
  id: string;
  startsAt: string;
  endsAt: string;
  kind: "stay" | "travel";
  label: string;
  address: string;
  precision: "exact" | "area";
  placeId: string | null;
  point: { latitude: number; longitude: number } | null;
};
export type Shortcut = {
  meetingTypeSlug: string;
  locationId: string;
  placeId: string;
  startsAt: string;
  endsAt: string;
  travelStatus?: "estimate" | "pending" | "verified";
};
type Artwork = {
  vectorUrl?: string | null;
  previewUrl?: string | null;
  cardUrl?: string | null;
};
type Day = {
  stayMergeGapMinutes?: number;
  city: string;
  timeZone: string;
  timeline?: TimelineItem[];
  timelineState?: "current" | "stale";
  timelineCheckedAt?: string | null;
  inPersonFull?: boolean | null;
  shortcuts?: Shortcut[];
  meetingTypes: Array<{ slug: string; name: string }>;
  placeOptions?: {
    profilePin?: Artwork | null;
    otherImage?: Artwork | null;
    places: Array<{
      image?: Artwork | null;
      pinImage?: Artwork | null;
      id: string;
      name: string;
      fullName: string;
      address: string;
      latitude: number | null;
      longitude: number | null;
    }>;
  } | null;
};
const text = (tag: string, value: string, className = "") => {
  const e = document.createElement(tag);
  e.textContent = value;
  e.className = className;
  return e;
};

export function mountCityDayTimeline(
  root: HTMLElement,
  day: Day,
  locale: string,
  mapKey: string,
  choose: (s: Shortcut) => void,
  resolveCityCenter?: (city: string) => Promise<[number, number] | null>,
) {
  const cs = locale === "cs";
  const say = (a: string, b: string) => (cs ? a : b);
  const time = new Intl.DateTimeFormat(cs ? "cs-CZ" : "en-GB", {
    timeZone: day.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const period = (s: string, e: string) =>
    `${time.format(new Date(s))}–${time.format(new Date(e))}`;
  root.replaceChildren(
    text("h2", say("Kde se budu pohybovat", "Where I will be")),
  );
  root.append(
    text(
      "p",
      say(
        "Pobyty a skutečně dostupné možnosti rezervace. Časy jsou uvedeny v pásmu ",
        "Planned stays and available booking options. Times are in ",
      ) + day.timeZone,
    ),
  );
  if (day.timelineState === "stale")
    root.append(
      text(
        "p",
        say(
          "Kalendář se nepodařilo obnovit. Pobyty nejsou aktuálně ověřené.",
          "The calendar could not be refreshed. These stays are not currently verified.",
        ),
        "notice",
      ),
    );
  const layout = document.createElement("div");
  layout.className = "city-timeline-layout";
  root.append(layout);
  const list = document.createElement("ol");
  list.className = "city-timeline";
  layout.append(list);
  const mapElement = document.createElement("div");
  mapElement.className = "city-timeline-map";
  mapElement.setAttribute(
    "aria-label",
    say("Mapa pobytů", "Map of planned stays"),
  );
  const mapColumn = document.createElement("div");
  mapColumn.className = "city-timeline-map-column";
  layout.prepend(mapColumn);
  const showAll = document.createElement("button");
  showAll.type = "button";
  showAll.className = "button secondary";
  showAll.textContent = say("Zobrazit všechna místa", "Show all places");
  mapColumn.append(mapElement, showAll);
  const artwork = (value?: Artwork | null) =>
    value?.vectorUrl ?? value?.previewUrl ?? value?.cardUrl;
  const imageFor = (placeId: string | null) => {
    const place = day.placeOptions?.places.find((p) => p.id === placeId);
    const url = artwork(
      place?.image ?? (placeId === null ? day.placeOptions?.otherImage : null),
    );
    const img = document.createElement("img");
    if (
      !url ||
      !/^(https?:\/\/|\/|data:image\/(?:webp|png|jpeg|svg\+xml)[;,])/i.test(url)
    )
      return null;
    img.src = url;
    img.alt = "";
    img.className = "city-timeline-place-image";
    return img;
  };
  const rows = new Map<string, HTMLElement>();
  const items = mergePublicPresence(
    day.timeline ?? [],
    day.stayMergeGapMinutes ?? 5,
  );
  const groups = new Map<string, Shortcut[]>();
  for (const s of day.shortcuts ?? [])
    groups.set(s.startsAt, [...(groups.get(s.startsAt) ?? []), s]);
  const entries = [
    ...items.map((item) => ({ start: item.startsAt, item })),
    ...[...groups].map(([start, options]) => ({ start, options })),
  ].sort((a, b) => a.start.localeCompare(b.start));
  let map: L.Map | null = null;
  const markers = new Map<string, L.Layer>();
  const select = (ids: string[], focusMap: boolean) => {
    for (const [id, row] of rows) {
      row.classList.toggle("selected", ids.includes(id));
      if (row.getAttribute("role") === "button")
        row.setAttribute("aria-pressed", String(ids.includes(id)));
    }
    for (const marker of new Set(markers.values()))
      if (marker instanceof L.Marker)
        marker
          .getElement()
          ?.querySelector(".city-place-marker-image, .city-place-marker")
          ?.classList.toggle(
            "selected",
            [...markers].some(
              ([id, layer]) => layer === marker && ids.includes(id),
            ),
          );
    const item = items.find((i) => i.id === ids[0]);
    if (focusMap && map && item?.point)
      map.setView(
        [item.point.latitude, item.point.longitude],
        item.precision === "area" ? 13 : 15,
      );
    if (!focusMap)
      rows.get(ids[0]!)?.scrollIntoView({ block: "nearest", behavior: "auto" });
    markers.get(ids[0]!)?.openTooltip();
  };
  for (const entry of entries) {
    const row = document.createElement("li");
    list.append(row);
    if ("item" in entry) {
      const item = entry.item;
      row.className = `city-timeline-${item.kind}`;
      rows.set(item.id, row);
      const venueImage = item.kind === "stay" ? imageFor(item.placeId) : null;

      row.append(
        text("span", period(item.startsAt, item.endsAt), "city-timeline-time"),
      );
      const name =
        item.kind === "travel"
          ? say("Plánovaný přesun", "Planned travel")
          : item.precision === "exact"
            ? item.label
            : say("Jiné místo", "Other place") +
              (item.label ? ` · ${item.label}` : "");
      const content = document.createElement("div");
      content.className = "city-timeline-place-content";
      const details = document.createElement("div");
      details.className = "city-timeline-place-details";
      if (venueImage) details.append(venueImage);
      details.append(content);
      row.append(details);
      content.append(text("strong", name));
      if (item.kind === "travel")
        content.append(
          text(
            "small",
            day.inPersonFull
              ? say(
                  "Plánovaný přesun mezi schůzkami.",
                  "Planned travel between meetings.",
                )
              : say(
                  "Po nové rezervaci se plán přesunu přizpůsobí.",
                  "The travel plan adjusts after a new booking.",
                ),
          ),
        );
      if (item.precision === "exact" && item.address)
        content.append(text("p", item.address));
      if (item.point) {
        row.tabIndex = 0;
        row.setAttribute("role", "button");
        row.setAttribute("aria-pressed", "false");
        row.setAttribute(
          "aria-label",
          `${period(item.startsAt, item.endsAt)} ${name} · ${say("Zvýraznit v mapě", "Highlight on map")}`,
        );
        row.onclick = () => {
          if (map) select([item.id], true);
        };
        row.onkeydown = (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            row.click();
          }
        };
      }
    } else {
      row.className = "city-timeline-availability";
      row.append(
        text(
          "strong",
          say("Možnosti rezervace od ", "Booking options from ") +
            time.format(new Date(entry.start)),
        ),
      );
      const cards = document.createElement("div");
      cards.className = "city-shortcuts";
      row.append(cards);
      for (const s of entry.options) {
        const type = day.meetingTypes.find((t) => t.slug === s.meetingTypeSlug);
        const place = day.placeOptions?.places.find((p) => p.id === s.placeId);
        if (!type || !place) continue;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "city-shortcut";
        button.dataset.shortcut = s.startsAt;
        const venueImage = imageFor(place.id);
        if (venueImage) button.append(venueImage);
        const detail = document.createElement("span");
        detail.className = "city-shortcut-details";
        button.append(detail);
        detail.append(
          text("strong", type.name),
          text("span", period(s.startsAt, s.endsAt)),
          text("span", place.fullName || place.name),
        );
        if (s.travelStatus && s.travelStatus !== "verified")
          detail.append(
            text(
              "small",
              say("Počítá s odhadovaným přesunem", "Includes estimated travel"),
            ),
          );
        button.append(text("span", "→", "city-shortcut-arrow"));
        button.setAttribute(
          "aria-label",
          `${type.name} ${period(s.startsAt, s.endsAt)} ${place.fullName || place.name} · ${say("Vybrat místo a čas", "Choose place and time")}`,
        );
        button.onclick = () => choose(s);
        cards.append(button);
      }
    }
  }
  if (!entries.length)
    list.append(
      text(
        "li",
        say(
          "Pro tento den zatím není ověřený přehled pobytů.",
          "There is no verified schedule of stays for this day yet.",
        ),
      ),
    );
  if (day.shortcuts?.length === 0 && !day.inPersonFull)
    root.append(
      text(
        "p",
        say(
          "Pro tento den nyní nejsou dostupné rezervace na standardních místech. Další možnosti ověříte výběrem typu schůzky.",
          "No appointments at standard places are currently available. Choose a meeting type to check other options.",
        ),
      ),
    );
  const fallback = () => {
    map?.remove();
    map = null;
    showAll.disabled = true;
    mapElement.replaceWith(
      text(
        "p",
        say(
          "Mapa nyní není dostupná. Přehled a rezervace můžete použít níže.",
          "The map is unavailable. The schedule and booking options remain usable.",
        ),
        "notice",
      ),
    );
    for (const row of rows.values()) {
      row.removeAttribute("role");
      row.removeAttribute("tabindex");
      row.removeAttribute("aria-pressed");
      row.removeAttribute("aria-label");
      row.onclick = null;
      row.onkeydown = null;
    }
  };
  if (!mapKey) {
    fallback();
    return () => {};
  }
  try {
    map = L.map(mapElement, { scrollWheelZoom: false });
    const tiles = L.tileLayer(
      `https://api.mapy.com/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${encodeURIComponent(mapKey)}`,
      {
        maxZoom: 19,
        referrerPolicy: "origin",
        attribution:
          '<a href="https://api.mapy.com/copyright" target="_blank" rel="noopener">&copy; Seznam.cz a.s. a další</a>',
      },
    ).addTo(map);
    let failures = 0;
    tiles.on("tileerror", () => {
      if (++failures === 3) fallback();
    });
    const Logo = L.Control.extend({
      options: { position: "bottomleft" as const },
      onAdd() {
        const e = L.DomUtil.create(
          "a",
          "mapy-logo-control",
        ) as HTMLAnchorElement;
        e.href = "https://mapy.com/";
        e.target = "_blank";
        e.rel = "noopener";
        const img = document.createElement("img");
        img.src = "https://api.mapy.com/img/api/logo.svg";
        img.alt = "Mapy.com";
        e.append(img);
        return e;
      },
    });
    new Logo().addTo(map);
    const content = drawCityDayMap(map, day.placeOptions?.places ?? [], day, {
      locale,
      profilePin: day.placeOptions?.profilePin,
      selectStay: (ids) => select(ids, false),
    });
    const bounds = content.bounds;
    for (const [id, marker] of content.presenceMarkers) markers.set(id, marker);
    const fitAll = () => {
      if (bounds.isValid())
        map?.fitBounds(bounds.pad(0.18), {
          maxZoom: 14,
          paddingTopLeft: [82, 100],
          paddingBottomRight: [70, 50],
        });
      else {
        const point =
          standardCityMapCenters[day.city.trim().toLocaleLowerCase()];
        if (point) map?.setView(point, 12);
        else if (resolveCityCenter)
          void resolveCityCenter(day.city).then((center) => {
            if (!map) return;
            if (center) map.setView(center, 12);
            else fallback();
          });
        else fallback();
      }
      select([], false);
    };
    showAll.onclick = fitAll;
    fitAll();
  } catch {
    fallback();
  }
  return () => {
    map?.remove();
    map = null;
  };
}
